import {
  Field,
  fieldRegistry,
  utils,
  type FieldConfig,
  type FieldValidator,
} from 'blockly/core';

const DEFAULT_COLOUR = '#ff0000';
const HEX_COLOUR_PATTERN = /^#([0-9a-fA-F]{6})$/;

export interface FieldBeginnerColourConfig extends FieldConfig {
  colour?: string;
}

export interface FieldBeginnerColourFromJsonConfig extends FieldBeginnerColourConfig {
  name?: string;
}

function normalizeColour(value: unknown): string {
  if (typeof value !== 'string') {
    return DEFAULT_COLOUR;
  }
  const trimmed = value.trim();
  if (HEX_COLOUR_PATTERN.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  return DEFAULT_COLOUR;
}

/** Beginner colour field — click opens a native colour picker. */
export class FieldBeginnerColour extends Field<string> {
  override EDITABLE = true;
  override SERIALIZABLE = true;

  private borderSize_ = 20;

  constructor(
    value?: string | typeof Field.SKIP_SETUP,
    validator?: FieldValidator<string> | null,
    config?: FieldBeginnerColourConfig,
  ) {
    super(Field.SKIP_SETUP, validator, config);
    if (value === Field.SKIP_SETUP) {
      this.setValue(normalizeColour(config?.colour));
      return;
    }
    this.setValue(normalizeColour(value ?? config?.colour));
  }

  protected override initView(): void {
    this.createBorderRect_();
    if (this.borderRect_) {
      this.borderRect_.setAttribute('rx', '4');
      this.borderRect_.setAttribute('ry', '4');
      this.borderRect_.style.strokeWidth = '1px';
    }
    this.updateSize_();
    this.applyColour();
  }

  protected override updateSize_(_margin?: number): void {
    const size = this.borderSize_;
    this.size_ = new utils.Size(size, size);
    if (this.borderRect_) {
      this.borderRect_.setAttribute('width', String(size));
      this.borderRect_.setAttribute('height', String(size));
    }
  }

  /** Keep the swatch filled with the selected colour (Blockly otherwise paints it white). */
  override applyColour(): void {
    const colour = this.getValue() ?? DEFAULT_COLOUR;
    if (this.borderRect_) {
      this.borderRect_.style.fill = colour;
      this.borderRect_.setAttribute('fill', colour);
      this.borderRect_.style.stroke = '#333333';
    }
  }

  protected override getText_(): string {
    return this.getValue() ?? DEFAULT_COLOUR;
  }

  protected override doClassValidation_(newValue?: unknown): string | null {
    if (typeof newValue !== 'string' || !HEX_COLOUR_PATTERN.test(newValue.trim())) {
      return null;
    }
    return newValue.trim().toLowerCase();
  }

  protected override doValueUpdate_(newValue: string): void {
    super.doValueUpdate_(newValue);
    this.applyColour();
  }

  override saveState(): string {
    return this.getValue() ?? DEFAULT_COLOUR;
  }

  override loadState(state: string): void {
    this.setValue(normalizeColour(state));
  }

  private getPickerAnchorRect_(): DOMRect {
    const clickTarget = this.getClickTarget_();
    if (clickTarget instanceof Element) {
      return clickTarget.getBoundingClientRect();
    }
    if (this.borderRect_) {
      return this.borderRect_.getBoundingClientRect();
    }
    const bbox = this.getScaledBBox();
    return new DOMRect(bbox.left, bbox.top, bbox.right - bbox.left, bbox.bottom - bbox.top);
  }

  protected override showEditor_(): void {
    // Native OS colour picker only — never show Blockly's DropDownDiv panel.
    // Anchor the hidden input on the field so the picker opens near the block.
    const rect = this.getPickerAnchorRect_();
    const left = Math.max(0, Math.round(rect.left));
    const top = Math.max(0, Math.round(rect.bottom + 4));
    const width = Math.max(24, Math.round(rect.width));
    const height = Math.max(24, Math.round(rect.height));

    const colourInput = document.createElement('input');
    colourInput.type = 'color';
    colourInput.value = this.getValue() ?? DEFAULT_COLOUR;
    colourInput.setAttribute('aria-label', 'Choose colour');
    colourInput.style.cssText = [
      'position:fixed',
      `left:${left}px`,
      `top:${top}px`,
      `width:${width}px`,
      `height:${height}px`,
      'opacity:0',
      'border:0',
      'padding:0',
      'margin:0',
      'pointer-events:none',
      'z-index:10000',
    ].join(';');
    document.body.appendChild(colourInput);

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) {
        return;
      }
      cleanedUp = true;
      colourInput.remove();
    };

    const applyValue = () => {
      this.setValue(normalizeColour(colourInput.value));
      this.applyColour();
    };

    colourInput.addEventListener('input', applyValue);
    colourInput.addEventListener('change', () => {
      applyValue();
      cleanup();
    });
    // Delay cleanup so cancel/close still allows a final change event to apply.
    colourInput.addEventListener('blur', () => {
      window.setTimeout(cleanup, 200);
    });

    colourInput.focus();
    if (typeof colourInput.showPicker === 'function') {
      colourInput.showPicker();
    } else {
      colourInput.click();
    }
  }

  override getAriaTypeName(): string | null {
    return 'Colour picker';
  }

  override getAriaValue(): string | null {
    return this.getValue();
  }

  static override fromJson(options: FieldBeginnerColourFromJsonConfig): FieldBeginnerColour {
    const colour =
      typeof options.colour === 'string'
        ? options.colour
        : typeof (options as { value?: unknown }).value === 'string'
          ? (options as { value: string }).value
          : DEFAULT_COLOUR;
    return new FieldBeginnerColour(normalizeColour(colour), undefined, options);
  }
}

fieldRegistry.register('field_beginner_colour', FieldBeginnerColour);
